import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, resolveDirective as _resolveDirective, createVNode as _createVNode, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "file-container pre" }
const _hoisted_2 = { class: "file-container-header" }
const _hoisted_3 = { class: "header-path" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["id"]
const _hoisted_6 = { key: 0 }
const _hoisted_7 = {
  key: 1,
  style: {"display":"flex","justify-content":"center"}
}
const _hoisted_8 = { key: 0 }
const _hoisted_9 = {
  key: 1,
  style: {"display":"flex","justify-content":"center"}
}

import { ref, inject, capitalize, onMounted, computed } from "vue";
import Button from 'primevue/button';
import 'vue-json-pretty/lib/styles.css';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import cookie from 'cookie';
import {filesize} from 'filesize';
import {Time} from '@jupyterlab/coreutils/src/time';
import scroll from 'scroll-into-view-if-needed'

import { ContentsManager, Contents } from '@jupyterlab/services';


export default /*@__PURE__*/_defineComponent({
  __name: 'FilePanel',
  props: [
  "entries",
  "sortby",
],
  emits: [
  "open-file"
],
  setup(__props, { expose: __expose, emit: __emit }) {


const curDir = ref<string>('.');

const emit = __emit;

const props = __props;

const contentManager = new ContentsManager({});

const cookies = cookie.parse(document.cookie);
const xsrfCookie = cookies._xsrf;

const showToast = inject<any>('show_toast');

const fileInput = ref<HTMLInputElement|undefined>(undefined);
const uploadForm = ref<HTMLFormElement|undefined>(undefined);
const files = ref<any[]|undefined>(undefined);
const tableLoading = ref<boolean>(false);

const formatDate = (dateString) => {
  return dateString !== null ? Time.formatHuman(dateString) : '-';
}

const formatSize = (size) => {
  return size !== null ? filesize(size, {spacer: ""}) : '-';
}

const openFileSelection = () => {
   fileInput.value?.click();
}

const displayPath = computed(() => {
  const path = curDir.value === '.' ? '' : curDir.value;
  return `/${path}`;
});

const getFileIconClass = (obj) => {
  const classes = [];
  if (obj.type === 'file') {
    classes.push('pi', 'pi-file');
  }
  else if (obj.type === 'directory') {
    classes.push('pi', 'pi-folder');
  }
  else if (obj.type === 'notebook') {
    classes.push('notebook-icon');
  }

  return classes;
}

const doubleClick = ({data}) => {
  if (data.type === 'directory') {
    curDir.value = data.path;
    refreshFiles();
  }
  else if (data.type === 'notebook') {
    if (window.confirm(`Open notebook ${data.name}?\n\nOpening this file will replace your current notebook and reset the session. Proceed?`)) {
      getFileContents(data).then((json) => {
        emit('open-file', json, data.path);
      })
    }
  }
  else {
    showToast({
      title: 'Unable to handle file',
      detail: 'Unable to handle this file. Please choose a different file.',
      severity: 'warning',
      life: 2500,
    })
  }
}

const getFileContents = async (fileData) => {
  tableLoading.value = true;
  try {
    const fileRecord = await contentManager.get(fileData.path);
    tableLoading.value = false;
    return fileRecord.content;
  } catch(e) {
    tableLoading.value = false;
    showToast({
      title: 'Invalid File',
      detail: 'Unable to load. Please check file contains valid ipynb json data.',
      severity: 'error',
      life: 10000
    });
  }
}

const fileDragOver = (event: DragEvent) => {
  // Prevent default to allow dropping if a file is being dragged. Otherwise don't allow dropping here.
  const hasFile = [...event.dataTransfer.items].filter((i) => (i.kind === 'file')).length > 0;
  if (hasFile) {
    event.preventDefault();
  }
};

const fileDrop = async (event: DragEvent) => {
  // Prevent default behavior (Prevent file from being opened)
  event.preventDefault();
  await uploadFiles(event.dataTransfer.files);
}

const onSelectFilesForUpload = async () => {
  // Fetch files from form element
  const fileList = uploadForm.value['uploadfiles']?.files;
  await uploadFiles(fileList);
}

const uploadFiles = async (files: FileList) => {
  const results = [];
  const promises = Array.from(files).map(async (file) => {
    const fullPath = `${curDir.value}/${file.name}`;
    const bytes = [];
    const reader = file.stream().getReader();
    var chunk = (await reader.read()).value;
    while (chunk?.length > 0) {
      bytes.push(Array.from(chunk, (byte) => String.fromCharCode(byte)).join(""));
      chunk = (await reader.read()).value;
    }
    const type = (file.type !== "" ? file.type : "application/octet-stream");
    const content = btoa(bytes.join(""));
    const format = 'base64';

    const fileObj: Partial<Contents.IModel> = {
      type,
      format,
      content,
    };

    let result;
    try {
      result = await contentManager.save(fullPath, fileObj);
    }
    catch(e) {
      showToast({
        title: 'Upload failed',
        detail: `Unable to upload file "${fullPath}": ${e}`,
        severity: 'error',
        life: 8000
      });
      return;
    }
    if (result && result.created && result.size) {
      showToast({
        title: 'Upload complete',
        detail: `File "${result.path}" (${result.size} bytes) successfully uploaded.`,
        severity: 'success',
        life: 4000
      });
    }
    else {
      showToast({
        title: 'Upload failed',
        detail: `Unable to upload file "${fullPath}".`,
        severity: 'error',
        life: 8000
      });
    }
  });
  // Wait for all uploads to complete, then refresh the file list.
  await Promise.all(promises);
  await refreshFiles();
  return results;
}

const downloadFile = async (file) => {
  let url = await contentManager.getDownloadUrl(file.path);
  // Ensure we are downloading. Add the download query param
  if (!/download=/.test(url)) {
    if (/\?/.test(url)) {
      url = url + "&download=1"
    }
    else {
      url = url + "?download=1"
    }
  }
  window.location.href = url;
};

const refreshFiles = async () => {
  tableLoading.value = true;
  const contents = await contentManager.get(curDir.value);
  const records = contents.content;
  records.sort((a, b) => {
    // Sort directories to the top
    if(a.type === 'directory' && b.type !== 'directory') {
      return -1;
    }
    else if (b.type === 'directory' && a.type !== 'directory') {
      return 1;
    }
    // Then sort by name
    return a.name.localeCompare(b.name)
  });
  if (curDir.value !== ".") {
    const parentDir = contentManager.normalize(`${curDir.value}/..`);
    records.splice(0, 0,
      {
        "name": "..",
        "path": parentDir,
        "last_modified": null,
        "created": null,
        "content": null,
        "format": null,
        "mimetype": null,
        "size": null,
        "writable": true,
        "hash": null,
        "hash_algorithm": null,
        "type": "directory"
      }
    );
  }
  files.value = records;
  tableLoading.value = false;
};

const flashFile = async (path: string) => {
  const nameSpan = document.getElementById(`file-list::${path}`)
  if (!nameSpan) {
    // File panel isn't populated, or is in some other directory.
    return;
  }
  const row = nameSpan.closest('tr');
  if (!row) {
    // If namespan exists, this should exist, but just in case err on the side of gracefully not flashing if it can't be
    // found.
    return;
  }
  scroll(row, {behavior: "smooth"});
  row.classList.add("flash");
  row.addEventListener("animationend", () => row.classList.remove("flash"), {once: true});
  row.style.backgroundColor = 'rgba(255, 0, 0, 0)';

}

onMounted(async () => {
  await refreshFiles();
});

__expose({
  refresh: refreshFiles,
  flashFile,
});


return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", null, [
        _cache[0] || (_cache[0] = _createTextVNode("Contents of: ")),
        _createElementVNode("span", _hoisted_3, _toDisplayString(displayPath.value), 1)
      ]),
      _cache[1] || (_cache[1] = _createElementVNode("span", { style: {"flex":"1"} }, null, -1)),
      _withDirectives(_createVNode(_unref(Button), {
        onClick: openFileSelection,
        icon: "pi pi-cloud-upload",
        size: "small"
      }, null, 512), [
        [
          _directive_tooltip,
          {value: 'Files to upload', showDelay: 300},
          void 0,
          { bottom: true }
        ]
      ]),
      _createElementVNode("form", {
        ref_key: "uploadForm",
        ref: uploadForm
      }, [
        _createElementVNode("input", {
          onChange: onSelectFilesForUpload,
          ref_key: "fileInput",
          ref: fileInput,
          type: "file",
          multiple: "",
          style: {"display":"none"},
          name: "uploadfiles"
        }, null, 544),
        _createElementVNode("input", {
          type: "hidden",
          name: "_xsrf",
          value: _unref(xsrfCookie)
        }, null, 8, _hoisted_4)
      ], 512),
      _withDirectives(_createVNode(_unref(Button), {
        onClick: refreshFiles,
        icon: "pi pi-refresh",
        size: "small"
      }, null, 512), [
        [
          _directive_tooltip,
          {value: 'Refresh files ', showDelay: 300},
          void 0,
          { bottom: true }
        ]
      ])
    ]),
    _createVNode(_unref(DataTable), {
      value: files.value,
      size: "small",
      stripedRows: "",
      class: "file-table",
      removableSort: "",
      onRowDblclick: doubleClick,
      resizableColumns: "",
      columnResizeMode: "fit",
      rowHover: "",
      loading: tableLoading.value,
      onDragover: fileDragOver,
      onDrop: fileDrop
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Column), {
          header: "",
          class: "download-column"
        }, {
          header: _withCtx(() => _cache[2] || (_cache[2] = [
            _createElementVNode("span", {
              class: "pi pi-cloud-download",
              style: {"font-weight":"lighter"}
            }, null, -1)
          ])),
          body: _withCtx((slotProps) => [
            (slotProps.data.type === 'file' || slotProps.data.type === 'notebook')
              ? _withDirectives((_openBlock(), _createBlock(_unref(Button), {
                  key: 0,
                  icon: "pi pi-cloud-download",
                  class: "download-button",
                  onClick: ($event: any) => (downloadFile(slotProps.data))
                }, null, 8, ["onClick"])), [
                  [
                    _directive_tooltip,
                    {value: 'Download', showDelay: 0, hideDelay: 0},
                    void 0,
                    { left: true }
                  ]
                ])
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "name",
          header: "Name",
          class: "filename-column",
          sortable: ""
        }, {
          body: _withCtx((slotProps) => [
            _createElementVNode("span", {
              class: _normalizeClass([...getFileIconClass(slotProps.data), 'file-icon', slotProps.data.type])
            }, null, 2),
            _createElementVNode("span", {
              id: `file-list::${slotProps.data.path}`,
              class: _normalizeClass(["file-name", slotProps.data.type])
            }, _toDisplayString(slotProps.data.name), 11, _hoisted_5)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "last_modified",
          header: "Last Modified",
          sortable: "",
          style: {"max-width":"25%"}
        }, {
          body: _withCtx((slotProps) => [
            (slotProps.data.last_modified)
              ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_6, [
                  _createTextVNode(_toDisplayString(formatDate(slotProps.data.last_modified)), 1)
                ])), [
                  [_directive_tooltip, slotProps.data.last_modified]
                ])
              : (_openBlock(), _createElementBlock("span", _hoisted_7, " - "))
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "size",
          header: "Size",
          sortable: "",
          style: {"max-width":"25%"}
        }, {
          body: _withCtx((slotProps) => [
            (slotProps.data.size)
              ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_8, [
                  _createTextVNode(_toDisplayString(formatSize(slotProps.data.size)), 1)
                ])), [
                  [_directive_tooltip, `${slotProps.data.size} bytes`]
                ])
              : (_openBlock(), _createElementBlock("span", _hoisted_9, " - "))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["value", "loading"])
  ]))
}
}

})